// TanStack Query wrappers for the Memberships feature.
// Mirrors the voucher feature's hook conventions — tenant-scoped cache keys,
// 30s stale time, keepPreviousData for paginated lists, cache patching on
// mutation success.

import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useAuthStore } from '@/stores/use-auth-store';
import { membershipsApi } from './api';
import type {
  CustomerMembership,
  EnrollCustomerInput,
  EnrolledMemberList,
  MembershipAggregateStats,
  MembershipPlan,
  MembershipPlanCreateInput,
  MembershipPlanListParams,
  MembershipPlanListResult,
  MembershipPlanStats,
  MembershipPlanUpdateInput,
} from './types';

function useTenantId(): string {
  return useAuthStore((s) => s.profile?.tenant_id ?? 'anonymous');
}

export function useMembershipPlanList(
  params: MembershipPlanListParams,
): UseQueryResult<MembershipPlanListResult> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [
      'memberships',
      tenantId,
      'list',
      params.page ?? 1,
      params.search ?? '',
      params.status ?? '',
      params.tier ?? '',
    ],
    queryFn: () => membershipsApi.list(params),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useMembershipPlan(id: string | undefined): UseQueryResult<MembershipPlan | null> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['memberships', tenantId, 'detail', id],
    queryFn: () => (id ? membershipsApi.get(id) : Promise.resolve(null)),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useMembershipAggregateStats(): UseQueryResult<MembershipAggregateStats> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['memberships', tenantId, 'aggregate-stats'],
    queryFn: () => membershipsApi.aggregateStats(),
    staleTime: 30_000,
  });
}

export function useMembershipPlanStats(
  id: string | undefined,
): UseQueryResult<MembershipPlanStats> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['memberships', tenantId, 'stats', id],
    queryFn: () => membershipsApi.stats(id!),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useMembershipPlanMembers(
  id: string | undefined,
  page: number,
): UseQueryResult<EnrolledMemberList> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['memberships', tenantId, 'members', id, page],
    queryFn: () => membershipsApi.enrolledMembers(id!, page),
    enabled: Boolean(id),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useCreateMembershipPlan() {
  const qc = useQueryClient();
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: (input: MembershipPlanCreateInput) => membershipsApi.create(input),
    onSuccess: (created) => {
      qc.setQueryData(['memberships', tenantId, 'detail', created.id], created);
      qc.invalidateQueries({ queryKey: ['memberships', tenantId, 'list'] });
    },
  });
}

export function useUpdateMembershipPlan(id: string) {
  const qc = useQueryClient();
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: (input: MembershipPlanUpdateInput) => membershipsApi.update(id, input),
    onSuccess: (updated) => {
      qc.setQueryData(['memberships', tenantId, 'detail', id], updated);
      qc.invalidateQueries({ queryKey: ['memberships', tenantId, 'list'] });
    },
  });
}

export function useSetMembershipPlanActive(id: string) {
  const qc = useQueryClient();
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: (isActive: boolean) => membershipsApi.setActive(id, isActive),
    onSuccess: (updated) => {
      qc.setQueryData(['memberships', tenantId, 'detail', id], updated);
      qc.invalidateQueries({ queryKey: ['memberships', tenantId, 'list'] });
    },
  });
}

export function useDeleteMembershipPlan(id: string) {
  const qc = useQueryClient();
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: () => membershipsApi.remove(id),
    onSuccess: () => {
      qc.removeQueries({ queryKey: ['memberships', tenantId, 'detail', id] });
      qc.invalidateQueries({ queryKey: ['memberships', tenantId, 'list'] });
    },
  });
}

// ─── Customer-side enrollment (used by Customer 360 Memberships tab) ─────

/**
 * The customer's currently active membership (null if none). Keyed under the
 * customer detail namespace so enroll/cancel invalidations refetch it.
 */
export function useCustomerActiveMembership(
  customerId: string | undefined,
): UseQueryResult<CustomerMembership | null> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['customers', tenantId, 'detail', customerId, 'active-membership'],
    queryFn: () => membershipsApi.getActiveMembership(customerId!),
    enabled: Boolean(customerId),
    staleTime: 30_000,
  });
}

export function useEnrollCustomerInPlan(customerId: string) {
  const qc = useQueryClient();
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: (input: EnrollCustomerInput) => membershipsApi.enrollCustomer(customerId, input),
    onSuccess: (_result: CustomerMembership) => {
      qc.invalidateQueries({ queryKey: ['customers', tenantId, 'detail', customerId] });
      qc.invalidateQueries({ queryKey: ['memberships', tenantId] });
    },
  });
}

export function useCancelCustomerEnrollment(customerId: string) {
  const qc = useQueryClient();
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: (reason?: string) => membershipsApi.cancelEnrollment(customerId, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customers', tenantId, 'detail', customerId] });
      qc.invalidateQueries({ queryKey: ['memberships', tenantId] });
    },
  });
}
